import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { Autocomplete } from "../../../components/Autocomplete";
import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/GroupRounded";
import { Table as CustomTable } from "../../../components/Table";
import {
  getInstances,
  getGroups,
  updateGroups,
} from "../../../services/whatsapp";
import { useStyles } from "./styles";
import { Group, Instance } from "../../../types/whatsapp";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Button from "@mui/material/Button";
import { useToast } from "../../../hooks/useToast";
import { useDialogConfirmation } from "../../../hooks/useDialogConfirmation";
import asyncForEach from "../../../utils/asyncForEach";
import DialogProgress from "../DialogProgress";
import DialogParticipants from "./DialogParticipants";
import DialogEdit from "./DialogEdit";

interface OptionsFilterStatus {
  value: number;
  label: string;
}

const OPTIONSFILTERSTATUS: OptionsFilterStatus[] = [
  { value: 1, label: "ativo" },
  { value: 0, label: "inativo" },
];

type GroupsProps = {
  instanceId?: number;
};

export function Groups({ instanceId }: GroupsProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const dialog = useDialogConfirmation();

  const [groupsUpdated, setGroupsUpdated] = useState<number>(0);
  const [dialogProgress, setDialogProgress] = useState<boolean>(false);
  const [dialogParticipants, setDialogParticipants] = useState<boolean>(false);
  const [dialogEdit, setDialogEdit] = useState<boolean>(false);
  const [searchNameOrNumber, setSearchNameOrNumber] = useState<string>("");
  const [selectedInstace, setSelectedInstance] = useState<Instance>();
  const [selectedStatus, setSelectedStatus] = useState<OptionsFilterStatus>();
  const [selectedGroups, setSelectedGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group>();

  const styles = useStyles();
  const instances = useQuery("instances", getInstances);
  const groups = useQuery(
    ["groups", selectedInstace?.id, selectedStatus?.value, searchNameOrNumber],
    () =>
      getGroups(selectedInstace?.id!, selectedStatus?.value, searchNameOrNumber)
  );

  const bulkUpdateGroups = useMutation(updateGroups);

  const actives = useMemo(() => {
    return groups.data?.filter((data) => Boolean(data.ACTIVE)).length;
  }, [groups.data]);

  const deactives = useMemo(() => {
    return groups.data?.filter((data) => !Boolean(data.ACTIVE)).length;
  }, [groups.data]);

  const handleUpdateStatusGroups = useCallback(
    async (active: boolean) => {
      try {
        setDialogProgress(true);
        await asyncForEach(selectedGroups, async (row) => {
          await bulkUpdateGroups.mutateAsync({
            ...row,
            active: active ? 1 : 0,
          });
          setGroupsUpdated((prev) => prev + 1);
        });
        await queryClient.invalidateQueries([
          "groups",
          selectedInstace?.id,
          selectedStatus?.value,
          searchNameOrNumber,
        ]);
        toast.show("Grupos atualizado com sucesso!!!", {
          severity: "success",
        });
      } catch (err) {
        toast.show("Erro ao atualizar os grupos!!!", { severity: "error" });
      } finally {
        setDialogProgress(false);
        setSelectedGroups([]);
        setGroupsUpdated(0);
      }
    },
    [
      toast,
      searchNameOrNumber,
      selectedStatus,
      selectedInstace,
      queryClient,
      bulkUpdateGroups,
      selectedGroups,
    ]
  );

  const handleSelectedGroups = (groups: Group[]) => {
    const data = groups.map((group) => ({
      client: group.INSTANCE,
      id: group.ID,
    }));
    setSelectedGroups(data);
  };

  const handleActivateGroups = useCallback(() => {
    dialog.show("Ativar grupos", "Deseja ativar os grupos selecionados?", {
      onConfirmate: () => {
        handleUpdateStatusGroups(true);
        dialog.close();
      },
    });
  }, [dialog, handleUpdateStatusGroups]);

  const handleDeactivateGroups = useCallback(() => {
    dialog.show(
      "Desativar grupos",
      "Deseja desativar os grupos selecionados?",
      {
        onConfirmate: () => {
          handleUpdateStatusGroups(false);
          dialog.close();
        },
      }
    );
  }, [dialog, handleUpdateStatusGroups]);

  const handleParticipants = useCallback((group: Group) => {
    setDialogParticipants(true);
    setSelectedGroup(group);
  }, []);

  const handleEdit = useCallback((group: Group) => {
    setDialogEdit(true);
    setSelectedGroup(group);
  }, []);

  const handleEditGroup = useCallback(async () => {
    await queryClient.invalidateQueries([
      "groups",
      selectedInstace?.id,
      selectedStatus?.value,
      searchNameOrNumber,
    ]);
  }, [queryClient, selectedInstace, selectedStatus, searchNameOrNumber]);

  useEffect(() => {
    if (instanceId && instances.data) {
      const instance = instances.data.find(
        (instance) => instance.id === instanceId
      );
      if (instance) {
        setSelectedInstance(instance);
      }
    }
  }, [instanceId, instances.data]);

  return (
    <Box>
      <Grid
        px={"1.5rem"}
        display={"flex"}
        alignItems={"baseline"}
        justifyContent={"space-between"}
      >
        <Grid className={styles.gridFilters}>
          <TextField
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            label="Pesquisar por nome"
            sx={{
              "&.MuiTextField-root": {
                width: "19.69rem",
              },
            }}
            value={searchNameOrNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchNameOrNumber(e.target.value);
            }}
          />

          <Autocomplete
            size="small"
            label={"Pesquisar por instância"}
            clearOnBlur={false}
            options={instances.data}
            getOptionLabel={(option) => option.nome}
            value={selectedInstace}
            onChange={(_, option) => {
              setSelectedInstance(option);
            }}
            sx={{ width: "20.69rem" }}
          />

          <Autocomplete
            size="small"
            label={"Filtrar por status"}
            clearOnBlur={false}
            options={OPTIONSFILTERSTATUS}
            getOptionLabel={(option) => option.label}
            value={selectedStatus}
            onChange={(_, option) => {
              setSelectedStatus(option);
            }}
            sx={{ width: "15.37rem" }}
          />
        </Grid>
      </Grid>

      <Grid mt={"2.5rem"} container height={"8.875rem"}>
        <Grid item xs={4} className={styles.gridContactsInfo}>
          <Paper className={styles.paperContactsInfo}>
            <Typography className={styles.typographyBigNumber}>
              {groups.isLoading ? (
                <CircularProgress variant="indeterminate" />
              ) : (
                groups.data?.length
              )}
            </Typography>
            <Typography className={styles.typographyTitle}>Grupos</Typography>
            <Typography className={styles.typographySubTitle}>
              Total de grupos
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4} className={styles.gridContactsInfo}>
          <Paper className={styles.paperContactsInfo}>
            <Typography className={styles.typographyBigNumber}>
              {groups.isLoading ? (
                <CircularProgress variant="indeterminate" />
              ) : (
                actives
              )}
            </Typography>
            <Typography className={styles.typographyTitle}>Ativos</Typography>
            <Typography className={styles.typographySubTitle}>
              Total de grupos ativos
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={4} className={styles.gridContactsInfo}>
          <Paper className={styles.paperContactsInfo}>
            <Typography className={styles.typographyBigNumber}>
              {groups.isLoading ? (
                <CircularProgress variant="indeterminate" />
              ) : (
                deactives
              )}
            </Typography>
            <Typography className={styles.typographyTitle}>Inativos</Typography>
            <Typography className={styles.typographySubTitle}>
              Total de grupos inativos
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Collapse in={Boolean(selectedGroups.length)} unmountOnExit>
        <Stack
          spacing={2}
          direction="row"
          bgcolor="#F3F4FB"
          px={2}
          py={2}
          borderBottom="1px solid rgba(224, 224, 224, 1)"
          alignItems="center"
        >
          <Typography>
            {selectedGroups.length}{" "}
            {`contato${selectedGroups.length > 1 ? "s" : ""}`}{" "}
            {`selecionado${selectedGroups.length > 1 ? "s" : ""}`}
          </Typography>
          <Button
            onClick={handleActivateGroups}
            size="small"
            variant="outlined"
          >
            Ativar grupos
          </Button>
          <Button
            onClick={handleDeactivateGroups}
            variant="outlined"
            size="small"
          >
            Desativar grupos
          </Button>
        </Stack>
      </Collapse>

      <CustomTable
        toolbar={false}
        pageSize={10}
        data={groups.data || []}
        isLoading={groups.isFetching}
        headerStyle={{
          backgroundColor: "#F3F4FB",
          padding: "10px 0px",
        }}
        selection
        onSelectionChange={handleSelectedGroups}
        columns={[
          {
            field: "instancia",
            title: "Instância",
            align: "center",
            render: (rowData: Group) => (
              <Typography className={styles.typographyCellTable}>
                {rowData.INSTANCE_NAME}
              </Typography>
            ),
          },
          {
            field: "status",
            title: "Status",
            align: "center",
            render: (rowData: Group) => (
              <Chip
                label={rowData.ACTIVE ? "Ativo" : "Inativo"}
                size="small"
                sx={{
                  padding: "0px 0.5rem",
                  gap: "0.625rem",
                  borderRadius: "0.25rem",
                  lineHeight: "22px",
                  letterSpacing: "0.05rem",
                  fontSize: "12px",
                  background: rowData.ACTIVE ? "#52C41A" : "#B02F2F",
                  "& .MuiChip-label": {
                    padding: 0,
                    color: "#FFF",
                  },
                }}
              />
            ),
          },
          {
            field: "nome",
            title: "Nome",
            align: "center",
            render: (rowData: Group) => (
              <Typography className={styles.typographyCellTable}>
                {rowData.NAME}
              </Typography>
            ),
          },
          {
            field: "nome",
            title: "",
            align: "right",
            render: (rowData: Group) => (
              <Stack direction="row-reverse" spacing={1}>
                <Tooltip title="Editar" placement="top">
                  <IconButton
                    onClick={() => {
                      handleEdit(rowData);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Participantes" placement="top">
                  <IconButton
                    onClick={() => {
                      handleParticipants(rowData);
                    }}
                  >
                    <GroupIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
          },
        ]}
        localization={{
          header: {
            actions: "",
          },
          pagination: {
            labelRowsSelect: "linhas",
            labelDisplayedRows: "{count} de {from}-{to}",
            firstTooltip: "Primeira página",
            previousTooltip: "Página anterior",
            nextTooltip: "Próxima página",
            lastTooltip: "Última página",
          },
        }}
      />
      <DialogProgress
        open={dialogProgress}
        total={selectedGroups.length}
        progress={groupsUpdated}
        text="grupos atualizado de"
      />
      <DialogParticipants
        group={selectedGroup}
        open={dialogParticipants}
        onClose={() => {
          setDialogParticipants(false);
        }}
      />
      {dialogEdit && (
        <DialogEdit
          group={selectedGroup}
          open={dialogEdit}
          onSave={handleEditGroup}
          onClose={() => {
            setDialogEdit(false);
          }}
        />
      )}
    </Box>
  );
}
